import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import api from "@/lib/api";
import { useWs } from "@/contexts/WebSocketContext";

export function useRecentValidations() {
  const [validations, setValidations] = useState([]);
  const [newIds, setNewIds] = useState(new Set());
  const ws = useWs();

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/validations/recent?limit=30");
      setValidations(data);
    } catch (err) {
      console.error("Failed to load recent validations:", err);
    }
  }, []);

  // Initial load
  useEffect(() => { load(); }, [load]);

  // WebSocket: prepend new validations in real-time
  useEffect(() => {
    if (!ws) return;
    return ws.on("new_validation", (v) => {
      setValidations((prev) => [v, ...prev].slice(0, 30));
      setNewIds((prev) => new Set(prev).add(v.id));
      // Clear highlight after 4s
      setTimeout(() => {
        setNewIds((prev) => {
          const next = new Set(prev);
          next.delete(v.id);
          return next;
        });
      }, 4000);
    });
  }, [ws]);

  return { validations, reload: load, newIds };
}

export function useSettings() {
  const [settings, setSettings] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/settings");
        if (!cancelled) setSettings(data);
      } catch (err) {
      console.error("Failed to load settings:", err);
    }
    })();
    return () => { cancelled = true; };
  }, []);
  return settings;
}

export function useRiskLabels(settings) {
  return useMemo(() => {
    if (!settings) return {};
    return {
      normal: settings.risk_label_normal || "Normal",
      suspicious: settings.risk_label_suspicious || "Suspicious",
      fraud: settings.risk_label_fraud || "Fraud",
    };
  }, [settings]);
}

export function useCustomRiskLevels(settings) {
  return useMemo(() => {
    if (!settings) return null;
    return settings.custom_risk_levels || null;
  }, [settings]);
}
